import { create } from 'zustand';
import { createConsumer, Subscription } from '@rails/actioncable';
import type { Notification, NotificationWebSocketMessage } from '@/types';
import { notificationService } from '@/services';
import { useAuthStore } from './authStore';
import { API_CONFIG } from '@/services/config';

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  hasMore: boolean;
  page: number;
  subscription: Subscription | null;
  isConnected: boolean;
}

interface NotificationActions {
  fetchNotifications: (reset?: boolean) => Promise<void>;
  fetchUnreadCount: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
  addNotification: (notification: Notification) => void;
  setUnreadCount: (count: number) => void;
  connect: () => void;
  disconnect: () => void;
  reset: () => void;
}

type NotificationStore = NotificationState & NotificationActions;

const initialState: NotificationState = {
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  hasMore: true,
  page: 1,
  subscription: null,
  isConnected: false,
};

export const useNotificationStore = create<NotificationStore>((set, get) => ({
  ...initialState,

  fetchNotifications: async (reset = false) => {
    const { isLoading, hasMore, page } = get();
    if (isLoading || (!reset && !hasMore)) return;

    const currentPage = reset ? 1 : page;
    set({ isLoading: true });

    try {
      const response = await notificationService.getNotifications({
        page: currentPage,
        per_page: 20,
      });

      const newNotifications = response.data;
      const { total_pages, unread_count } = response.meta;

      set((state) => ({
        notifications: reset
          ? newNotifications
          : [...state.notifications, ...newNotifications],
        unreadCount: unread_count,
        hasMore: currentPage < total_pages,
        page: currentPage + 1,
        isLoading: false,
      }));
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      set({ isLoading: false });
    }
  },

  fetchUnreadCount: async () => {
    try {
      const response = await notificationService.getUnreadCount();
      set({ unreadCount: response.data.unread_count });
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  },

  markAsRead: async (id: string) => {
    try {
      await notificationService.markAsRead(id);
      set((state) => ({
        notifications: state.notifications.map((n) =>
          n.id === id ? { ...n, read: true, readAt: new Date().toISOString() } : n
        ),
        unreadCount: Math.max(0, state.unreadCount - 1),
      }));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  },

  markAllAsRead: async () => {
    try {
      await notificationService.markAllAsRead();
      set((state) => ({
        notifications: state.notifications.map((n) => ({
          ...n,
          read: true,
          readAt: new Date().toISOString(),
        })),
        unreadCount: 0,
      }));
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  },

  deleteNotification: async (id: string) => {
    try {
      await notificationService.deleteNotification(id);
      set((state) => {
        const notification = state.notifications.find((n) => n.id === id);
        const wasUnread = notification && !notification.read;
        return {
          notifications: state.notifications.filter((n) => n.id !== id),
          unreadCount: wasUnread
            ? Math.max(0, state.unreadCount - 1)
            : state.unreadCount,
        };
      });
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  },

  addNotification: (notification: Notification) => {
    set((state) => ({
      notifications: [notification, ...state.notifications],
      unreadCount: notification.read ? state.unreadCount : state.unreadCount + 1,
    }));
  },

  setUnreadCount: (count: number) => {
    set({ unreadCount: count });
  },

  connect: () => {
    const { subscription, isConnected } = get();
    if (subscription || isConnected) return;

    const token = useAuthStore.getState().token;
    if (!token) return;

    try {
      // Get the WebSocket URL from the current page origin
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${wsProtocol}//${window.location.host}${API_CONFIG.backend.cable}?token=${encodeURIComponent(token)}`;

      const consumer = createConsumer(wsUrl);

      const newSubscription = consumer.subscriptions.create(
        { channel: 'NotificationChannel' },
        {
          connected() {
            console.log('Connected to NotificationChannel');
            set({ isConnected: true });
          },
          disconnected() {
            console.log('Disconnected from NotificationChannel');
            set({ isConnected: false });
          },
          received(data: NotificationWebSocketMessage) {
            const store = get();

            switch (data.type) {
              case 'subscribed':
                set({ unreadCount: data.unread_count });
                break;

              case 'new_notification':
                if (data.notification) {
                  store.addNotification(data.notification);
                }
                break;

              case 'notification_read':
                set((state) => ({
                  notifications: state.notifications.map((n) =>
                    n.id === data.notification_id
                      ? { ...n, read: true, readAt: new Date().toISOString() }
                      : n
                  ),
                  unreadCount: data.unread_count,
                }));
                break;

              case 'all_notifications_read':
                set((state) => ({
                  notifications: state.notifications.map((n) => ({
                    ...n,
                    read: true,
                    readAt: new Date().toISOString(),
                  })),
                  unreadCount: 0,
                }));
                break;

              case 'unread_count_update':
                set({ unreadCount: data.unread_count });
                break;
            }
          },
        }
      );

      set({ subscription: newSubscription });
    } catch (error) {
      console.error('Failed to connect to NotificationChannel:', error);
    }
  },

  disconnect: () => {
    const { subscription } = get();
    if (subscription) {
      subscription.unsubscribe();
      set({ subscription: null, isConnected: false });
    }
  },

  reset: () => {
    const { subscription } = get();
    if (subscription) {
      subscription.unsubscribe();
    }
    set(initialState);
  },
}));
